import React, { useEffect, useState, useContext } from 'react';
import { Divider, Spin, Table, message, Typography, Image } from 'antd';
import _ from 'lodash';
import { InvestmentItem, InvestmentRecord, InvestmentTypeEnum, NameItem } from 'types/investor';
import { TabComponentProps } from 'types/common';
import { AlignType } from 'rc-table/lib/interface';
import api from 'api';
import { get, getFile, post } from 'utils/fetch';
import { USDollar } from 'utils/format';
import { FundLink } from 'components/Links';
import { GlobalContext } from 'GlobalContext';
const { Text } = Typography;

const InvestmentBreakdown: React.FC<TabComponentProps> = ({ nameid }) => {
    const { accessToken } = useContext(GlobalContext);
    const [activeInvestments, setActiveInvestments] = useState<InvestmentRecord[]>([]);
    const [investorImage, setInvesetorImage] = useState<any>('');
    const [messageApi, contextHolder] = message.useMessage();
    const [nameidMapping, setNameIDMapping] = useState<Record<string, NameItem>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const displayErrorMessage = (message: string) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };


    async function fetchInvestorInvestments(nameid: number) {
        setLoading(true);
        await get(api.investor.getActiveInvestments(nameid), async (data: InvestmentRecord[]) => {
            //console.log("fetchInvestorInvestments", data)
            const nameid_need_translation = [nameid.toString()]
            for (const record of data) {
                if (record.name) record.to_nameid = record.name;
                else if (record.fund_nameid) record.to_nameid = record.fund_nameid;
                else if (record.subfund_nameid) record.to_nameid = record.subfund_nameid;
                else if (record.holding_company_nameid) record.to_nameid = record.holding_company_nameid;
                else record.to_nameid = '';
                nameid_need_translation.push(record.to_nameid); // commented for testing purposes
            }
            setActiveInvestments(data);
            await fetchNameIDMapping(Array.from(new Set(nameid_need_translation))); // commented for testing purposes
        }, (errors) => {
            for (const err of errors) {
                displayErrorMessage(err.message);
            }
        });
    }
    async function fetchNameIDMapping(nameidList: string[]) {
        await post(api.misc.getNameIDMapping, {
            payload: nameidList
        }, (data) => {
            if (data.length !== nameidList.length) return
            const updateObj: Record<string, NameItem> = {}
            nameidList.forEach((e, i) => {
                updateObj[e] = data[i];
            });
            setNameIDMapping({
                ...nameidMapping,
                ...updateObj,
            })
        }, (errors: any[]) => errors.forEach(err => {
            displayErrorMessage(err.message);
        }
        ));
        setLoading(false);
    }


    const investmentItemColumns = [{
        title: 'Name',
        dataIndex: 'legal_name',
        render: (value: string, record: InvestmentItem, index: number) => {
            return `${value} (${record['investor_nameid']})`
        },
        width: '250px',
    }, {
        title: 'To Fund',
        dataIndex: 'to_nameid',
        render: (nameid: string, record: InvestmentItem) => {
            return <FundLink nameid={nameid} text={nameid in nameidMapping ? nameidMapping[nameid].name : nameid} />;
        },
        width: '350px',
    }, {
        title: 'Type',
        dataIndex: 'type',
    }, {
        title: 'Status',
        dataIndex: 'status',
    }, {
        title: 'Total Invested Amount ($)',
        dataIndex: 'amount',
        width: '200px',
        align: 'right' as AlignType,
        sorter: (a: InvestmentItem, b: InvestmentItem) => {
            return (a.amount || 0) - (b.amount || 0);
        },
        render: (val: number) => USDollar.format(val)
    },];

    const renderInvestmentItemsByType = (type: InvestmentTypeEnum) => {
        const groupedData = _.groupBy(
            activeInvestments.filter(x => x.type === type), (record) => {
                return [record.to_nameid, record.type]
            })
        let groupedDataWithSum: InvestmentItem[] = []
        for (const key of Object.keys(groupedData)) {
            const [to_nameid, type] = key.split(',')
            let groupInvestmentAmount = 0;
            let legal_name = ''
            let investor_nameid = ''
            let status = ''
            for (const item of groupedData[key]) {
                legal_name = String(item.legal_name);
                investor_nameid = item.investor_nameid;
                groupInvestmentAmount += item.amount;
                status = item.status;
            }
            groupedDataWithSum.push({
                investor_nameid,
                legal_name,
                to_nameid,
                type,
                status,
                amount: groupInvestmentAmount,
                investment_amount: groupInvestmentAmount
            } as InvestmentItem);
        }
        return <Table
            bordered
            dataSource={groupedDataWithSum}
            columns={investmentItemColumns}
            pagination={false}
            summary={(rowData: readonly InvestmentItem[]) => {
                let total = 0;

                rowData.forEach(({ type, amount }) => {
                    total += amount;
                });

                if (total < 0) total = -total;

                return (
                    <>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>Total Invested</Table.Summary.Cell>
                            <Table.Summary.Cell align='right' index={1} colSpan={5}>
                                <Text type="success">{USDollar.format(total)}</Text>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    </>
                );
            }}
        />
    }

    async function fetchInvestorImage(nameid: string) {
        getFile(api.investor.getInvestorImage(nameid), (blob) => {
            let reader = new FileReader();
            reader.onload = function () { setInvesetorImage(this.result) };
            reader.readAsDataURL(blob);
        }, (errors) => { }, accessToken);
    }

    const renderInvestorImage = () => investorImage ? <Image
        width={300}
        height={300}
        src={investorImage}
        fallback="data:image/png;base64,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"
    /> : null

    useEffect(() => {
        if (nameid) {
            fetchInvestorInvestments(parseInt(nameid));// comeback later to change type to number
            fetchInvestorImage(nameid);
        }
    }, [nameid]);

    if (loading) return <Spin />
    else if (!nameid) return <span>Please select a valid user</span>;

    return <>
        {contextHolder}
        <div className="investor-investments-breakdown">
            <h2>Active Investment Summaries for {nameidMapping[nameid]?.name || nameid}</h2>
            <h3>Equity Invested (Active)</h3>
            {renderInvestmentItemsByType(InvestmentTypeEnum.Equity)}
            <Divider />
            <h3>Debt Invested</h3>
            {renderInvestmentItemsByType(InvestmentTypeEnum.Debt)}
            <div className="investor-investments-charts">
                <h3>Your Investments Charts</h3>
                <h4>Investor-Property Relational Image (Dynamically Generated)</h4>
                {renderInvestorImage()}
            </div>
        </div>
    </>
}

export default InvestmentBreakdown;